/**
 * Exam result (brief §exam: `/exam/:id/result`), which is also the exam
 * wrapper (pedagogy spec §7.3.1, §7.3.8): the score card with the G/VG ticks
 * and the gap to the next grade; "Var tappade du poängen?", the live pattern
 * and at most two suggestions side by side; one plan line; one save. Below:
 * the per-problem table, the folded per-chapter table and — while no
 * *startable* suggestion is showing (pedagogy review A1: a suggestion must
 * never be the only thing on the page and dead-end) — the link to practise
 * the weakest chapters.
 */
import clsx from "clsx";
import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router";
import { EXAM_TOTAL_POINTS, THRESHOLD_G, THRESHOLD_VG } from "@shared/exam";
import { dropInactiveTopics, lostPoints, suggestNext } from "@shared/examWrapper";
import { useTopicsAndChapters } from "@client/api/topics";
import { useLocalizedText } from "@client/components/Localized";
import {
  Badge,
  Button,
  Card,
  PageHeader,
  ProgressBar,
  Spinner,
  Table,
  useToast,
} from "@client/components/ui";
import { useLang, useT, useTp } from "@client/i18n";
import { messageForError } from "@client/lib/errors";
import { formatNumber, formatPercent, formatPoints } from "@client/lib/format";
import { verdictTone } from "@client/lib/verdict";
import { useExamDetail, useSaveReflection } from "./api";
import { paperLabel } from "./paper";
import type { GradeResultDto, SessionDto } from "./types";
import { CausesCard } from "./wrapper/CausesCard";
import {
  clearReflectionDraft,
  initialReflection,
  loadReflectionDraft,
  saveReflectionDraft,
} from "./wrapper/draft";
import {
  addedToToday,
  sittingOf,
  toggleCause,
  wrapperProblems,
  type CauseState,
} from "./wrapper/model";
import { PatternCard } from "./wrapper/PatternCard";
import { PlanLineCard } from "./wrapper/PlanLineCard";
import { START_HREF, SuggestedNextCard } from "./wrapper/SuggestedNextCard";

interface ChapterRow {
  chapter: number;
  score: number;
  maxScore: number;
}

/** Where the G and VG lines sit on the score bar. */
const GRADE_TICKS = [
  { grade: "G", at: THRESHOLD_G / EXAM_TOTAL_POINTS },
  { grade: "VG", at: THRESHOLD_VG / EXAM_TOTAL_POINTS },
] as const;

export function ExamResultPage() {
  const { id } = useParams<{ id: string }>();
  const detailQuery = useExamDetail(id);

  if (detailQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }
  const detail = detailQuery.data;
  if (!detail) return <Navigate to="/exam" replace />;
  const { session } = detail;
  if (session.status === "in_progress") return <Navigate to="/exam/session" replace />;
  if (session.status === "abandoned") return <Navigate to="/exam" replace />;
  if (session.status !== "graded" || !session.results) {
    return <Navigate to={`/exam/${session.id}/grade`} replace />;
  }
  return (
    <GradedResult
      key={session.id}
      session={session}
      results={session.results}
      latestGraded={detail.latestGraded ?? true}
    />
  );
}

function GradedResult({
  session,
  results,
  latestGraded,
}: {
  session: SessionDto;
  results: GradeResultDto[];
  latestGraded: boolean;
}) {
  const t = useT();
  const { lang } = useLang();
  const topicsQuery = useTopicsAndChapters();

  const resultByQuestion = new Map(results.map((r) => [r.questionId, r]));
  const chapterRows: ChapterRow[] = [];
  for (const sq of session.questions) {
    const result = resultByQuestion.get(sq.question.id);
    if (!result) continue;
    const row = chapterRows.find((r) => r.chapter === sq.question.chapter);
    if (row) {
      row.score += result.score;
      row.maxScore += result.maxScore;
    } else {
      chapterRows.push({
        chapter: sq.question.chapter,
        score: result.score,
        maxScore: result.maxScore,
      });
    }
  }
  chapterRows.sort((a, b) => a.chapter - b.chapter);
  const weakChapters = chapterRows
    .filter((r) => r.maxScore > 0 && r.score / r.maxScore < 0.5)
    .map((r) => r.chapter);
  // The chapter link steps aside once a *startable* suggestion is showing (pedagogy review
  // A1): a suggestion must never be the only thing on the page without a "Starta", and the
  // student must never be left with no CTA at all. Every suggestion kind starts something
  // since step 2.8c; the check stays so a kind without a link would keep the chapter link up.
  const savedProblems = wrapperProblems(session, results);
  const savedSuggestions = suggestNext(
    savedProblems,
    session.reflection?.causes ?? {},
    sittingOf(session, results),
  );
  const savedTopics = topicsQuery.data?.topics;
  const keptSavedSuggestions = savedTopics
    ? dropInactiveTopics(savedSuggestions, new Set(savedTopics.map((topic) => topic.id)))
    : savedSuggestions.filter((s) => s.kind !== "setup" && s.kind !== "accuracy");
  const hasStartableSuggestion = keptSavedSuggestions.some(
    (suggestion) => START_HREF[suggestion.kind](suggestion) !== null,
  );

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader title={t("exam.result.title")} eyebrow={paperLabel(session, t)} />

      {/*
        Review-A Important 3: the client-side auto-submit explanation lives
        on the *grade* page now (router state, `ExamGradePage`), where the
        student actually lands right after time runs out. This banner is a
        second, independent signal read straight from the saved session, so
        it still shows up after a reload, or when the student never saw the
        grade-page banner in the first place (they may have already been on
        the grade page from an earlier visit). `late` is the server's own
        "submitted after the deadline" flag (routes.ts) — true for a manual
        late hand-in too, not only an automatic one, so the wording below
        never claims "automatically" the way the grade-page banner can. The
        server also sets `late` when it auto-expires a session the student
        walked away from, so that case is covered by this same flag.
      */}
      {session.late && (
        <p
          role="status"
          className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
        >
          {t("exam.result.lateSubmitted")}
        </p>
      )}

      <ScoreCard session={session} />

      <ReflectionSection session={session} results={results} latestGraded={latestGraded} />

      <Table
        caption={t("exam.result.perProblem")}
        rowKey={(row) => String(row.slot)}
        columns={[
          { key: "slot", header: t("exam.lobby.pools.problem"), cell: (row) => row.slot },
          {
            key: "chapter",
            header: t("exam.lobby.pools.chapters"),
            cell: (row) =>
              session.questions.find((q) => q.slot === row.slot)?.question.chapter ?? "—",
          },
          {
            key: "score",
            header: t("exam.result.score"),
            cell: (row) => `${formatPoints(row.score, lang)} / ${formatPoints(row.maxScore, lang)}`,
          },
        ]}
        rows={results}
      />

      <details className="surface p-4">
        <summary className="cursor-pointer text-sm font-medium text-ink-700 dark:text-ink-200">
          {t("exam.result.perChapter")}
        </summary>
        <Table
          className="mt-3"
          caption={<span className="sr-only">{t("exam.result.perChapter")}</span>}
          rowKey={(row) => String(row.chapter)}
          columns={[
            { key: "chapter", header: t("exam.lobby.pools.chapters"), cell: (row) => row.chapter },
            {
              key: "score",
              header: t("exam.result.score"),
              cell: (row) =>
                `${formatPoints(row.score, lang)} / ${formatPoints(row.maxScore, lang)}`,
            },
            {
              key: "ratio",
              header: t("exam.result.ratio"),
              cell: (row) => formatPercent(row.maxScore > 0 ? row.score / row.maxScore : 0, lang),
            },
          ]}
          rows={chapterRows}
        />
      </details>

      {weakChapters.length > 0 && !hasStartableSuggestion && (
        <Card title={t("exam.result.practiceWeak.title")}>
          <p className="mb-3 text-sm text-ink-600 dark:text-ink-400">
            {t("exam.result.practiceWeak.body")}
          </p>
          {/* P1-10: starts at once. `state` marks the press as in-app, so a copied or Canvas link
              with `autostart=1` only fills the form in; "Ändra urval" opens the form itself. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link
              to={`/practice?chapters=${weakChapters.join(",")}&autostart=1`}
              state={{ autostart: true }}
              className="inline-flex items-center rounded-lg bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700 dark:bg-accent-600 dark:hover:bg-accent-700"
            >
              {t("exam.result.practiceWeak.link", { chapters: weakChapters.join(", ") })}
            </Link>
            <Link
              to={`/practice?chapters=${weakChapters.join(",")}`}
              className="text-sm text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
            >
              {t("exam.result.practiceWeak.change")}
            </Link>
          </div>
        </Card>
      )}
    </div>
  );
}

/** Causes, pattern, suggestions and the plan line, with one save (pedagogy spec §7.3.8). */
function ReflectionSection({
  session,
  results,
  latestGraded,
}: {
  session: SessionDto;
  results: GradeResultDto[];
  latestGraded: boolean;
}) {
  const t = useT();
  const toast = useToast();
  const localize = useLocalizedText();
  const topicsQuery = useTopicsAndChapters();
  const saveMutation = useSaveReflection(session.id);
  const [state, setState] = useState(() =>
    initialReflection(session.id, session.reflection ?? null),
  );
  // "Lägg till i dagens pass" worked in this visit (the refetched session catches up a moment later).
  const [addedNow, setAddedNow] = useState(false);

  const problems = useMemo(() => wrapperProblems(session, results), [session, results]);
  const anyLost = problems.some((problem) => lostPoints(problem) > 0);
  const topics = topicsQuery.data?.topics;
  const suggestions = useMemo(() => {
    if (!topics && !topicsQuery.isError) return null;
    const ranked = suggestNext(problems, state.causes, sittingOf(session, results));
    return topics
      ? dropInactiveTopics(ranked, new Set(topics.map((topic) => topic.id)))
      : ranked.filter((s) => s.kind !== "setup" && s.kind !== "accuracy");
  }, [problems, state.causes, session, results, topics, topicsQuery.isError]);
  const topicName = (topicId: string) => {
    const topic = topics?.find((entry) => entry.id === topicId);
    return topic ? localize(topic.name) : topicId;
  };

  function update(next: { causes: CauseState; plan: string }) {
    setState(next);
    saveReflectionDraft(session.id, { ...next, editedAt: Date.now() });
  }

  /** One save: the causes and the plan line as they stand, and `addToToday` when asked for. */
  async function save(addToToday = false) {
    if (saveMutation.isPending) return;
    const startedAt = Date.now();
    try {
      const res = await saveMutation.mutateAsync({
        causes: state.causes,
        plan: state.plan,
        ...(addToToday ? { addToToday: true as const } : {}),
      });
      const draft = loadReflectionDraft(session.id);
      if (draft && draft.editedAt > startedAt) {
        saveReflectionDraft(session.id, {
          ...draft,
          editedAt: Math.max(draft.editedAt, res.reflection.savedAt + 1),
        });
      } else {
        clearReflectionDraft(session.id);
      }
      if (addToToday) setAddedNow(true);
      else toast.success(t("common.saved"));
    } catch (error) {
      toast.error(messageForError(error, t));
    }
  }

  return (
    <div className="space-y-6">
      <div
        className={clsx("grid gap-6", anyLost && "lg:grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)]")}
      >
        <CausesCard
          problems={problems}
          answers={session.answers}
          causes={state.causes}
          onToggle={(questionId, cause, on) =>
            update({ ...state, causes: toggleCause(state.causes, questionId, cause, on) })
          }
        />
        {anyLost && (
          <div className="space-y-6">
            <PatternCard problems={problems} causes={state.causes} />
            {suggestions ? (
              <SuggestedNextCard
                suggestions={suggestions}
                problems={problems}
                causes={state.causes}
                topicName={topicName}
                examSessionId={session.id}
                onAddToToday={() => void save(true)}
                adding={saveMutation.isPending}
                added={addedNow || addedToToday(session.reflection ?? null)}
              />
            ) : (
              <Card title={t("exam.wrapper.next.title")}>
                <Spinner size="sm" />
              </Card>
            )}
          </div>
        )}
      </div>
      <PlanLineCard
        value={state.plan}
        onChange={(plan) => update({ ...state, plan })}
        onSubmit={() => void save()}
        latest={latestGraded}
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => void save()} loading={saveMutation.isPending}>
          {t("exam.wrapper.save")}
        </Button>
        <p className="text-sm text-ink-600 dark:text-ink-400">{t("exam.wrapper.private")}</p>
      </div>
    </div>
  );
}

/** Score out of `EXAM_TOTAL_POINTS`, verdict, G/VG ticks and the gap to the next grade. */
function ScoreCard({ session }: { session: SessionDto }) {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const total = session.totalScore ?? 0;
  const gap = session.gap ?? null;
  const gapPoints = (points: number) => formatNumber(points, lang, { maxDecimals: 1 });

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-3xl font-display text-ink-900 dark:text-ink-50">
            {formatPoints(total, lang)} / {formatPoints(EXAM_TOTAL_POINTS, lang)}
          </p>
          {!!session.bonusApplied && (
            <p className="text-sm text-ink-600 dark:text-ink-400">
              {tp("exam.result.bonusApplied", session.bonusApplied, {
                bonus: formatNumber(session.bonusApplied, lang, { maxDecimals: 1 }),
              })}
            </p>
          )}
          {gap && (
            <p className="text-sm text-ink-700 dark:text-ink-200">
              {"toVG" in gap
                ? tp("exam.result.toVG", gap.toVG, { points: gapPoints(gap.toVG) })
                : tp("exam.result.toG", gap.toG, { points: gapPoints(gap.toG) })}
            </p>
          )}
        </div>
        <Badge tone={verdictTone(session.verdict ?? "U")}>
          {t(`exam.verdict.${session.verdict ?? "U"}`)}
        </Badge>
      </div>
      <div className="relative mt-4">
        <ProgressBar
          value={Math.min(1, total / EXAM_TOTAL_POINTS)}
          label={t("exam.result.score")}
        />
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-2">
          {GRADE_TICKS.map((tick) => (
            <span
              key={tick.grade}
              data-tick={tick.grade}
              className="absolute top-0 h-2 w-0.5 bg-ink-900 dark:bg-ink-50"
              style={{ left: `${tick.at * 100}%` }}
            />
          ))}
        </div>
      </div>
      <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
        {t("exam.result.thresholds", {
          g: formatNumber(THRESHOLD_G, lang),
          vg: formatNumber(THRESHOLD_VG, lang),
        })}
      </p>
    </Card>
  );
}

export default ExamResultPage;
